import React, { useContext, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  IconButton,
  CopyToClipboardIcon,
  DownloadIcon,
  Button,
  WarningIcon
} from '@paypalcorp/pp-react';
import copy from 'copy-to-clipboard';
import { saveAs } from 'file-saver';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import styles from './CodeWrapper.module.less';
import './codeHighlightStyle.legacy.css';
import IntegrationPageSectionContext from '../StateManagement/IntegrationPageSectionContext';
import { trackTabClick, trackTabShown, trackButtonClick } from '../../../lib/fpti';
import { getBaseZipFile, getFileArray } from '../getBaseZipFile';
import { useSelector } from 'react-redux';
import { PARTNER } from '../../../lib/constants';

const CodeWrapper = ({ sectionIndex = 1 }) => {
  const {
    code: {
      consolidatedCode = [],
      language: { backend, frontend }
    },
    codeHighlight: { codeBlock },
    product
  } = useContext(IntegrationPageSectionContext);
  const [selectedTabIdx, setSelectedTabIdx] = useState(0);
  const [codeSnippetIconState, setCodeSnippetIconState] = useState({
    state: 'initial',
    srText: ''
  });
  const [downloadIconState, setDownloadIconState] = useState({
    state: 'initial',
    srText: ''
  });
  const [isHovered, setIsHovered] = useState(false);
  const [highlightedLines, setHighlightedLines] = useState([]);
  const appIntegrationType = useSelector((state) => state?.app?.appIntegrationType);
  const changeCodeIconState = (state, srText) => {
    setCodeSnippetIconState({
      state,
      srText
    });
  };
  const guideCodeWrapperRef = useRef(null);
  const handleMouseEnter = () => {
    setIsHovered(true);
    if (guideCodeWrapperRef.current) {
      guideCodeWrapperRef.current.style.overscrollBehavior = 'contain';
    }
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    if (guideCodeWrapperRef.current) {
      guideCodeWrapperRef.current.style.overscrollBehavior = '';
    }
  };
  useEffect(() => {
    return () => {
      if (guideCodeWrapperRef.current) {
        guideCodeWrapperRef.current.style.overscrollBehavior = '';
      }
    };
  }, []);
  const changeDownloadIconState = (state, srText, autoReset = false) => {
    setDownloadIconState({
      state,
      srText
    });
    if (autoReset) {
      setTimeout(() => {
        setDownloadIconState({
          state: 'initial',
          srText: ''
        });
      }, 3000);
    }
  };
  const clickHandler = (clickedTabIndex) => {
    setSelectedTabIdx(clickedTabIndex);
    // Recover fadedLines in current new file.
    setTimeout(() => {
      const $codeLines = document.getElementById(`code-snippet-${sectionIndex}-${clickedTabIndex}`)
        ?.children?.[0]?.children;
      if ($codeLines && $codeLines.length > 0) {
        Array.from($codeLines).forEach((element, index) => {
          $codeLines[index].className = '';
        });
      }
    }, 1);
  };
  const codeLineNumberProducer = (start, end) => {
    if (start === 1 && end === 1) return [];
    return Array.from({ length: end - start + 1 }).map((i, index) => index + start);
  };

  const handleCopyClick = () => {
    copy(consolidatedCode[selectedTabIdx]?.code);
    changeCodeIconState('success', 'Success');
    setTimeout(() => {
      changeCodeIconState('initial', '');
    }, 3000);
  };

  useEffect(() => {
    let fileIndex = consolidatedCode.map((code) => code.filename).indexOf(codeBlock.filename);
    fileIndex = fileIndex > 0 ? fileIndex : 0;
    const file = consolidatedCode[fileIndex > 0 ? fileIndex : 0];
    if (file) {
      const currentCodeBlock = file.metadata.find((code) => code.key === codeBlock.key);
      setSelectedTabIdx(fileIndex);
      if (currentCodeBlock) {
        setHighlightedLines(
          codeLineNumberProducer(currentCodeBlock.startLine || 0, currentCodeBlock.endLine || 0)
        );
      }
    }
  }, [codeBlock, consolidatedCode]);
  useEffect(() => {
    setTimeout(() => {
      // Scroll the highlighted codes into view
      const firstHighlightLineNum = highlightedLines[0];
      if (firstHighlightLineNum !== undefined) {
        // One code line height is about 20px. Leave 2 lines above the hightlighted part.
        document.getElementById('guide-code-wrapper').scrollTop = (firstHighlightLineNum - 4) * 20;
      }
    }, 1);
  }, [highlightedLines]);

  const handleDownload = async () => {
    changeDownloadIconState('processing', 'Downloading');
    let zip;
    try {
      zip = await getBaseZipFile({ appIntegrationType, product, frontend, backend });
    } catch {
      changeDownloadIconState('error', 'Error fetching', true);
    }

    const files = getFileArray(zip);
    const zipBasePath = files.find((file) => file.dir).name;

    let basePath = `${product}/`;
    const deepBasePath = files.find(
      (file) => file.name.toLowerCase() === `${basePath}${basePath}`.toLowerCase()
    );
    if (deepBasePath) basePath += zipBasePath;

    consolidatedCode.forEach((file) => {
      const fileBasePath = basePath;
      const fullPath = file?.filePath ? fileBasePath + file?.filePath : basePath;
      zip.file(`${fullPath}/${file?.filename}`, file?.code);
    });
    const content = await zip.generateAsync({ type: 'blob' });
    const fileName = [appIntegrationType === PARTNER ? PARTNER : '', product, frontend, backend]
      .filter(Boolean)
      .join('_');
    saveAs(content, fileName);
    changeDownloadIconState('success', 'Success', true);
  };

  const calculatedStyle = (lineNumber) => {
    let lineStyle = {};
    if (
      highlightedLines.length === 0 ||
      !highlightedLines.includes(lineNumber) ||
      selectedTabIdx !== consolidatedCode.map((code) => code.filename).indexOf(codeBlock.filename)
    ) {
      lineStyle = !isHovered ? 'non-highlighted-code' : 'non-highlighted';
    } else {
      lineStyle = 'highlighted';
    }
    return { highlight_status: [lineStyle] };
  };

  const languageMapper = {
    react: 'javascript'
  };

  const backgroundColor = (codeBlock.key || '').includes('base') ? 'black' : '#1e1e1e';

  return (
    <div className={styles.code_content}>
      <div className={styles.code_actions_bar}>
        <div className={styles.code_tabs}>
          {consolidatedCode.map((entry, tabIndex) => {
            return (
              <Button
                secondary
                className={`${styles.tab_nav_button} ${
                  tabIndex === selectedTabIdx ? styles.tab_nav_button_selected : ''
                }`}
                onClick={() => {
                  trackTabClick(entry.filename);
                  trackTabShown(entry.filename);
                  clickHandler(tabIndex);
                }}
                key={entry.filename}
              >
                {entry.filename}
              </Button>
            );
          })}
        </div>
        <div className={styles.code_btn_bar}>
          <div className={styles.code_action_btn}>
            <IconButton
              className={styles.icon_button}
              buttonType="secondary"
              size="lg"
              btnState={codeSnippetIconState.state}
              screenReaderText={codeSnippetIconState.srText}
              svgIconComponent={CopyToClipboardIcon}
              onClick={() => {
                handleCopyClick();
                const file = consolidatedCode[selectedTabIdx].filename;
                const { language } = consolidatedCode[selectedTabIdx];
                trackButtonClick('Copy', null, { file, language });
              }}
            >
              Copy
            </IconButton>
          </div>
          <div className={styles.code_action_btn} id={`download_code_btn_${sectionIndex}`}>
            <IconButton
              className={styles.icon_button}
              buttonType="secondary"
              size="lg"
              btnState={downloadIconState.state !== 'error' ? downloadIconState.state : 'initial'}
              screenReaderText={downloadIconState.srText}
              svgIconComponent={downloadIconState.state !== 'error' ? DownloadIcon : WarningIcon}
              onClick={() => {
                const file = consolidatedCode[selectedTabIdx].filename;
                const { language } = consolidatedCode[selectedTabIdx];
                trackButtonClick('Download', null, { file, language });
                handleDownload();
              }}
            >
              Download
            </IconButton>
          </div>
        </div>
      </div>
      {consolidatedCode[selectedTabIdx] && (
        <div
          ref={guideCodeWrapperRef}
          id="guide-code-wrapper"
          className={styles.guide_code_wrapper}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <SyntaxHighlighter
            language={
              languageMapper[consolidatedCode[selectedTabIdx].language] ||
              consolidatedCode[selectedTabIdx].language ||
              'javascript'
            }
            style={vscDarkPlus}
            showLineNumbers
            className={styles.code_snippet}
            id={`code-snippet-${sectionIndex}-${selectedTabIdx}`}
            data-testid="codeBlock"
            lineProps={calculatedStyle}
            wrapLines
            customStyle={{
              width: 'max-content',
              minWidth: '100%',
              backgroundColor
            }}
          >
            {consolidatedCode[selectedTabIdx].code}
          </SyntaxHighlighter>
        </div>
      )}
    </div>
  );
};
CodeWrapper.propTypes = {
  sectionIndex: PropTypes.number
};

CodeWrapper.defaultProps = {
  sectionIndex: 1
};

export default CodeWrapper;
